import { createContext, useState, useEffect, type FC, type ReactNode } from 'react';

type Listener = (isOpen: boolean) => void;
type RegisterModalType = (id: string) => void;
type ModalRegistry = {
    registerModal: RegisterModalType;
    unregisterModal: RegisterModalType;
    isAnyModalOpen: () => boolean;
    addListener: (listener: Listener) => () => void;
};

declare global {
    interface Window {
        __dibsModalRegistry?: ModalRegistry;
    }
}

const REGISTRY_EVENT_NAME = 'dibs-modal-registry-update';

const createModalRegistry = (): ModalRegistry => {
    let openModals = new Map<string, boolean>();
    const listeners = new Set<Listener>();

    const dispatchModalStateEvent = (): void => {
        const event = new CustomEvent(REGISTRY_EVENT_NAME, {
            detail: { modals: Array.from(openModals.entries()) },
        });
        window.dispatchEvent(event);
    };

    const updateLocalListeners = (): void => {
        const isOpen = openModals.size > 0;
        listeners.forEach(listener => listener(isOpen));
    };

    const handleEvent = (event: CustomEvent): void => {
        try {
            const modals = event.detail?.modals;
            if (modals) {
                openModals = new Map(modals);
                updateLocalListeners();
            }
        } catch (e) {
            // do nothing
        }
    };

    if (typeof window !== 'undefined') {
        window.removeEventListener(REGISTRY_EVENT_NAME, handleEvent as EventListener);
        window.addEventListener(REGISTRY_EVENT_NAME, handleEvent as EventListener);
    }

    return {
        registerModal: (modalId: string): void => {
            openModals.set(modalId, true);
            dispatchModalStateEvent();
            updateLocalListeners();
        },

        unregisterModal: (modalId: string): void => {
            if (openModals.has(modalId)) {
                openModals.delete(modalId);
                dispatchModalStateEvent();
                updateLocalListeners();
            }
        },

        isAnyModalOpen: (): boolean => openModals.size > 0,

        addListener: (listener: Listener): (() => void) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
};

const getOrCreateModalRegistry = (): ModalRegistry => {
    if (typeof window !== 'undefined' && window.__dibsModalRegistry) {
        return window.__dibsModalRegistry;
    }

    const registry = createModalRegistry();

    if (typeof window !== 'undefined') {
        window.__dibsModalRegistry = registry;
    }

    return registry;
};

const modalRegistry = getOrCreateModalRegistry();

export const useRegisterModal = (isOpen: boolean, id: string): void => {
    useEffect(() => {
        if (isOpen) {
            modalRegistry.registerModal(id);
        } else {
            modalRegistry.unregisterModal(id);
        }

        return () => {
            if (isOpen) {
                modalRegistry.unregisterModal(id);
            }
        };
    }, [isOpen, id]);
};

export const isAnyModalOpen = (): boolean => {
    return modalRegistry.isAnyModalOpen();
};

const ModalGlobalStateContext = createContext<{ modalOpenState: boolean }>({
    modalOpenState: false,
});

export const ModalGlobalStateProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [modalOpenState, setModalOpenState] = useState(modalRegistry.isAnyModalOpen());

    useEffect(() => {
        return modalRegistry.addListener(setModalOpenState);
    }, []);

    return <ModalGlobalStateContext value={{ modalOpenState }}>{children}</ModalGlobalStateContext>;
};
